import { useIntl } from 'react-intl';

import { FontWeights, Text } from '@calm-web/design-system';

import messages from '@/components/emotion-detector/messages';
import { JournalEntry } from '@/hooks/emotion-detector';

import { NOTES_PLACEHOLDER } from '../constants';
import {
	CorrectionText,
	DeleteButton,
	DeleteIcon,
	EntryCard,
	EntryDate,
	EntryEmotion,
	EntryHeader,
	EntrySummary,
	NotesLabel,
	NotesTextarea,
	NotesTitle,
} from '../styles';
import { formatEntryDate, getNoteKey } from '../utils';
import EntryImages from './EntryImages';

interface JournalEntryCardProps {
	entry: JournalEntry;
	notes: Record<string, string>;
	onNoteChange: (key: string, value: string) => void;
	onDeleteClick: (entryId: string) => void;
	isDeleting: boolean;
}

export default function JournalEntryCard({
	entry,
	notes,
	onNoteChange,
	onDeleteClick,
	isDeleting,
}: JournalEntryCardProps) {
	const { formatMessage } = useIntl();
	const noteKey = getNoteKey(entry.id);
	const displayDate = formatEntryDate(entry);

	return (
		<EntryCard>
			<DeleteButton onClick={() => onDeleteClick(entry.id)} disabled={isDeleting}>
				{isDeleting ? <span>⏳</span> : <DeleteIcon>🗑️</DeleteIcon>}
			</DeleteButton>

			<EntryHeader>
				<EntryDate>
					<Text>{displayDate}</Text>
				</EntryDate>
				<EntryEmotion>
					<Text weight={FontWeights.Demi}>
						{entry.emotion || 'neutral'}
						{entry.annotation && entry.annotation !== entry.emotion && (
							<CorrectionText>
								{formatMessage(messages.entryCorrected, { annotation: entry.annotation })}
							</CorrectionText>
						)}
					</Text>
				</EntryEmotion>
			</EntryHeader>

			{entry.summary && (
				<EntrySummary>
					<Text>{entry.summary}</Text>
				</EntrySummary>
			)}

			{entry.images && <EntryImages images={entry.images} />}

			<NotesLabel>
				<NotesTitle>
					<Text>{formatMessage(messages.logEntryYourNotes)}</Text>
				</NotesTitle>
				<NotesTextarea
					placeholder={NOTES_PLACEHOLDER}
					value={notes[noteKey] || ''}
					onChange={e => onNoteChange(noteKey, e.target.value)}
					rows={3}
				/>
			</NotesLabel>
		</EntryCard>
	);
}
